import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EmptyState, ErrorState, PageHeader } from '@/components/page';
import { Pagination } from '@/components/pagination';
import { AUDIT_ACTIONS, auditDetail, describeAction } from '@/features/audit/describe';
import { FilterSelect } from '@/features/register/filter-select';
import { api } from '@/lib/api';
import { can, meQuery } from '@/lib/auth';
import { formatDateTime } from '@/lib/labels';
import type { AuditEvent, Page } from '@/lib/types';

const TARGETS = [
  { value: 'case', label: 'Cases' },
  { value: 'entry', label: 'Entries' },
  { value: 'document', label: 'Documents' },
  { value: 'import', label: 'Imports' },
  { value: 'register', label: 'Searches & listings' },
  { value: 'user', label: 'Users & sign-in' },
  { value: 'client', label: 'Clients & keys' },
];

interface AuditSearch {
  action?: string;
  targetType?: string;
  from?: string;
  to?: string;
  page?: number;
}

const isDate = (v: unknown): v is string => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);

export const Route = createFileRoute('/_app/audit')({
  validateSearch: (s: Record<string, unknown>): AuditSearch => ({
    action: AUDIT_ACTIONS.includes(s.action as string) ? (s.action as string) : undefined,
    targetType: TARGETS.some((t) => t.value === s.targetType) ? (s.targetType as string) : undefined,
    from: isDate(s.from) ? s.from : undefined,
    to: isDate(s.to) ? s.to : undefined,
    page: Number(s.page) > 1 ? Math.floor(Number(s.page)) : undefined,
  }),
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!me || !can.readAudit(me)) throw redirect({ to: '/entries', search: {} });
  },
  component: AuditPage,
});

const PAGE_SIZE = 50;

function AuditPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const set = (patch: Partial<AuditSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch, page: 'page' in patch ? patch.page : undefined }) });

  const query = useQuery({
    queryKey: ['audit', search],
    queryFn: () =>
      api.get<Page<AuditEvent>>('/v1/audit-events', {
        action: search.action,
        targetType: search.targetType,
        from: search.from ? new Date(`${search.from}T00:00:00`).toISOString() : undefined,
        to: search.to ? new Date(`${search.to}T23:59:59.999`).toISOString() : undefined,
        page: search.page ?? 1,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader title="Audit log" description="A permanent record of everything added, changed, viewed, searched and signed in to. Personal data in old records is removed after six years." />
      <div className="mb-4 flex flex-wrap items-end gap-2">
        <FilterSelect label="Area" value={search.targetType} options={TARGETS} onChange={(targetType) => set({ targetType })} />
        <FilterSelect label="Action" value={search.action} options={AUDIT_ACTIONS.map((a) => ({ value: a, label: describeAction(a) }))} onChange={(action) => set({ action })} />
        <div className="flex items-center gap-2">
          <Label htmlFor="audit-from" className="text-[13px] font-normal text-muted-foreground">
            From
          </Label>
          <Input id="audit-from" type="date" className="h-8 w-auto" value={search.from ?? ''} onChange={(e) => set({ from: e.target.value || undefined })} />
          <Label htmlFor="audit-to" className="text-[13px] font-normal text-muted-foreground">
            to
          </Label>
          <Input id="audit-to" type="date" className="h-8 w-auto" value={search.to ?? ''} onChange={(e) => set({ to: e.target.value || undefined })} />
        </div>
      </div>

      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <div className="overflow-hidden rounded-xl border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted hover:bg-muted">
                <TableHead className="px-4">When</TableHead>
                <TableHead>Who</TableHead>
                <TableHead>What</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.isPending
                ? Array.from({ length: 10 }, (_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={4} className="px-4">
                        <Skeleton className="h-6 w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                : query.data.items.map((e) => (
                    <TableRow key={e.id} className="align-top">
                      <TableCell className="px-4 py-2.5 whitespace-nowrap text-muted-foreground tabular-nums">{formatDateTime(e.createdAt)}</TableCell>
                      <TableCell className="py-2.5">{e.actorName ?? (e.actorId === 'system' ? 'System' : e.actorId)}</TableCell>
                      <TableCell className="py-2.5">
                        <div>{describeAction(e.action)}</div>
                        {e.caseId && e.targetType !== 'register' ? (
                          <Link to="/cases/$caseId" params={{ caseId: e.caseId }} className="text-xs text-link underline-offset-4 hover:underline">
                            Open case
                          </Link>
                        ) : null}
                      </TableCell>
                      <TableCell className="max-w-[420px] py-2.5 text-[13px] whitespace-normal text-muted-foreground">
                        {auditDetail(e)}
                        {e.reason ? <div className="text-foreground">“{e.reason}”</div> : null}
                        {e.data ? (
                          <details className="mt-1">
                            <summary className="cursor-pointer text-xs">Raw data</summary>
                            <pre className="mt-1 max-h-64 overflow-auto rounded bg-muted p-2 font-mono text-[11px] whitespace-pre-wrap text-foreground">{JSON.stringify(e.data, null, 2)}</pre>
                          </details>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
          {query.data && query.data.items.length === 0 ? (
            <div className="p-4">
              <EmptyState title="Nothing recorded for these filters" />
            </div>
          ) : null}
          {query.data ? <Pagination page={query.data.page} pageSize={PAGE_SIZE} total={query.data.total} onPage={(page) => set({ page })} /> : null}
        </div>
      )}
    </>
  );
}
